import { ApiConnector } from './api.connector';
import type {
  IFortePreliminaryLimitResult,
  IPreApprovalBiometricSessionResponse,
  IPreApprovalCurrentStatus,
  IPreApprovalPrecheckResponse,
  IPreApprovalReissueTokenResponse,
  IPreApprovalValidateOtpResponse,
  TForteLimitStatus
} from './api.types/pre-approval.api-types';
import {
  type IPreApprovalCurrentStatusDto,
  toCurrentStatus
} from './mappers/pre-approval.mappers';

export type { IFortePreliminaryLimitResult, TForteLimitStatus };

class PreApprovalApi extends ApiConnector {
  /**
   * Для методов с preapproval-client-token 401 может значить и «протух основной Bearer»,
   * и «протух сам clientToken» — снаружи не отличить. this.call() тут всегда с
   * skipAuthRedirect: true (см. api.connector.ts — общий для автокредита/лизинга, его не
   * трогаем), поэтому сам он на 401 просто кидает ошибку без рефреша и логаута. Здесь пробуем
   * один раз освежить сессию и повторить: если дело было в Bearer — юзер не заметит; если в
   * clientToken — ошибка просто прокинется дальше, как и раньше.
   */
  private async callWithSessionRetry<Response>(
    params: {
      path: string;
      method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
      body?: unknown;
      params?: unknown;
      extraHeaders?: Record<string, string>;
    },
    alreadyRetried = false
  ): Promise<Response> {
    try {
      return await this.call<unknown, Response>({
        ...params,
        skipAuthRedirect: true
      });
    } catch (err) {
      const statusCode = (err as { statusCode?: number })?.statusCode;
      if (statusCode === 401 && !alreadyRetried) {
        const tokens = await this.refresh();
        if (tokens) return this.callWithSessionRetry<Response>(params, true);
      }
      throw err;
    }
  }

  precheck(body: {
    iin: string;
    phoneNumber: string;
    cityId: string;
  }): Promise<IPreApprovalPrecheckResponse> {
    return this.call({
      path: 'preapproval/precheck',
      method: 'post',
      body
    });
  }

  sendOtp({
    iin,
    phoneNumber
  }: {
    iin: string;
    phoneNumber: string;
  }): Promise<{ success: true }> {
    return this.call({
      path: 'preapproval/auth',
      method: 'post',
      body: { iin, phoneNumber }
    });
  }

  validateOtp({
    iin,
    phoneNumber,
    otp
  }: {
    iin: string;
    phoneNumber: string;
    otp: string;
  }): Promise<IPreApprovalValidateOtpResponse> {
    return this.call({
      path: 'preapproval/auth/validate-otp',
      method: 'post',
      body: { iin, phoneNumber, otp }
    });
  }

  createBiometricSession(
    clientId: string,
    data: { clientToken: string; salePointCity: string }
  ): Promise<IPreApprovalBiometricSessionResponse> {
    return this.callWithSessionRetry({
      path: `preapproval/${encodeURIComponent(clientId)}/biometric-session`,
      method: 'post',
      body: { salePointCity: data.salePointCity },
      extraHeaders: preapprovalTokenHeader(data.clientToken)
    });
  }

  initiateLimit(
    clientId: string,
    data: { biometricSessionUuid: string; clientToken: string }
  ): Promise<IFortePreliminaryLimitResult> {
    return this.callWithSessionRetry({
      path: `preapproval/${encodeURIComponent(clientId)}/limit`,
      method: 'post',
      body: { biometric_session_uuid: data.biometricSessionUuid },
      extraHeaders: preapprovalTokenHeader(data.clientToken)
    });
  }

  getLimitResult(
    clientId: string,
    data: { id: string; clientToken: string }
  ): Promise<IFortePreliminaryLimitResult> {
    return this.callWithSessionRetry({
      path: `preapproval/${encodeURIComponent(clientId)}/limit/${encodeURIComponent(data.id)}`,
      method: 'get',
      extraHeaders: preapprovalTokenHeader(data.clientToken)
    });
  }

  // Без clientToken — тихая проверка ДО прохождения OTP, самого токена ещё не существует.
  // Без iin/phoneNumber — бэкенд резолвит аккаунт по Bearer-сессии супераппа сам (см.
  // docs/specs/forte-preliminary-limit/07-pii-and-account-scoped-cache.md), фронту не нужно
  // ни спрашивать ИИН у клиента, ни хранить его где-либо, чтобы получить этот результат.
  // null, если валидного результата нет — это ожидаемый исход, не ошибка.
  checkLatestResult(): Promise<IFortePreliminaryLimitResult | null> {
    return this.call({
      path: 'preapproval/latest-limit',
      method: 'get'
    });
  }

  // Без clientToken — та же причина, что у checkLatestResult. Единственный источник правды
  // о текущем шаге, включая сроки кода и исход биометрии (бэкенд получает вебхук вендора).
  // null — аккаунт ещё не начинал флоу, ожидаемый исход.
  async getCurrentStatus(): Promise<IPreApprovalCurrentStatus | null> {
    const data = await this.call<
      undefined,
      IPreApprovalCurrentStatusDto | null
    >({
      path: 'preapproval/current',
      method: 'get'
    });
    return toCurrentStatus(data);
  }

  // No clientToken input — that's the point, this reissues one via the Bearer session.
  reissueClientToken(): Promise<IPreApprovalReissueTokenResponse> {
    return this.call({
      path: 'preapproval/current/client-token',
      method: 'get'
    });
  }
}

// Токен, выданный validate-otp — доказывает, что именно этот вызывающий прошёл OTP для
// данного clientId. Без него бэкенд отклонит запрос как принадлежащий чужому clientId.
function preapprovalTokenHeader(clientToken: string): Record<string, string> {
  return clientToken ? { 'preapproval-client-token': clientToken } : {};
}

export const preApprovalApi = new PreApprovalApi();
